import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logoStrip from '../assets/logo/logo-strip-tinted.png';
import twoTowers from '../assets/image/twoTowers.jpg';
import fourTowers from '../assets/image/fourTowers.jpg';

function Home() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "TQNY ALBYANAT — Saudi Data & AI Excellence";
  }, []);

  const services = [
    {
      num: "01",
      category: "Data & AI",
      title: "Enterprise Data & AI",
      description: "Governed data platforms, analytics engineering, and MLOps for Saudi banks, ministries, and enterprises.",
      link: "/services/"
    },
    {
      num: "02",
      category: "Cloud",
      title: "Technology & Cloud Services",
      description: "Cloud strategy, migration, and modernization — aligned with Saudi data residency mandates.",
      link: "/services/"
    },
    {
      num: "03",
      category: "Intelligence",
      title: "Applied Intelligence",
      description: "Arabic NLP, computer vision, forecasting, and automation — built for Saudi business contexts.",
      link: "/services/"
    },
    {
      num: "04",
      category: "Cloud Studio",
      title: "Cloud Acceleration Studio",
      description: "Your Fast Track to Agile, Scalable, and AI-Optimized Cloud.",
      link: "/services/"
    },
    {
      num: "05",
      category: "Trust",
      title: "Cybersecurity & Compliance",
      description: "NCA-aligned architecture, GRC, 24/7 SOC, and PDPL compliance for the Kingdom's most regulated organizations.",
      link: "/services/"
    },
    {
      num: "06",
      category: "People",
      title: "Advisory & Saudi Talent",
      description: "Strategic advisory and Saudization-focused talent that builds capability, not dependence.",
      link: "/contact"
    }
  ];

  // Sovereign Features
  const sovereignFeatures = [
    {
      num: "01",
      title: "Integrated Delivery",
      description: "Projects designed, deployed, and managed by teams who understand complex regulatory and business environments."
    },
    {
      num: "02",
      title: "Regulatory Fluency",
      description: "NCA, SAMA, PDPL, and SDAIA frameworks are integrated into how we architect — not bolted on as compliance afterthoughts."
    },
    {
      num: "03",
      title: "Capability Transfer",
      description: "Every engagement is designed to leave your team stronger. Knowledge transfer, training, and local capability building come standard."
    }
  ];

  // Compliance Items
  const complianceItems = [
    { name: "NCA-ECC", desc: "Essential Cybersecurity Controls" },
    { name: "PDPL", desc: "Personal Data Protection Law" },
    { name: "SAMA", desc: "Saudi Central Bank" },
    { name: "SDAIA", desc: "Data & AI Authority" },
    { name: "ISO 27001", desc: "Information Security" },
    { name: "ISO 9001", desc: "Quality Management" }
  ];

  // Vision Pillars
  const visionPillars = [
    {
      num: "01",
      title: "Digital Economy",
      description: "Supporting the region's ambition to become a leading digital economy and knowledge-based society."
    },
    {
      num: "02",
      title: "Local Capability",
      description: "Building lasting in-country expertise through training, knowledge transfer, and joint delivery models on every engagement."
    },
    {
      num: "03",
      title: "Data Sovereignty",
      description: "In-Kingdom delivery aligned with PDPL, NCA, and SDAIA frameworks — the standard our local and global clients require."
    }
  ];

  // Industries
  const industries = [
    { index: "01", name: "Government", meta: "Authorities & entities", category: "Public Sector" },
    { index: "02", name: "Banking", meta: "Local & International", category: "Finance" },
    { index: "03", name: "Telecom", meta: "Operators & providers", category: "Comms" },
    { index: "04", name: "Energy & Chemicals", meta: "Strategic sector", category: "Energy" },
    { index: "05", name: "Healthcare", meta: "Public & private", category: "Health" },
    { index: "06", name: "Retail & Consumer", meta: "Regional & global brands", category: "Retail" },
    { index: "07", name: "Insurance", meta: "Regulated entities", category: "Insurance" },
    { index: "08", name: "Manufacture", meta: "Industrial enterprises", category: "Industrial" }
  ];

  // Story Values
  const storyValues = [
    { num: "01", label: "Client-Promise First" },
    { num: "02", label: "Innovation with Purpose" },
    { num: "03", label: "Excellence & Ownership" },
    { num: "04", label: "Open Collaboration" },
    { num: "05", label: "Diverse Collaboration" },
    { num: "06", label: "Trusted Resilience" }
  ];

  return (
    <>
      {/* HERO SECTION */}
      <section className="hero">
        <div className="container-new">
          <div className="hero-grid">
            <div className="hero-text">
              <div className="hero-badge reveal d1">
                <span className="hero-badge-dot"></span>
                <span>Regionally-Headquartered · Globally Aligned</span>
              </div>
              <h1 className="reveal d2">Data-driven.<br />Outcome-aligned.</h1>
              <p className="hero-sub reveal d3">We help enterprises across Saudi Arabia and the wider region turn data and artificial intelligence into measurable business impact — combining local expertise with international standards of delivery.</p>
              <div className="hero-actions reveal d4">
                <Link to="/contact" className="btn-primary">Start a Conversation <span>→</span></Link>
                <a href="/services" className="btn-secondary">Explore Capabilities <span>→</span></a>
              </div>
              <div className="hero-meta reveal d4">
                <div className="hero-meta-item">
                  <div className="hero-meta-num">KSA+</div>
                  <div className="hero-meta-label">Regionally engaged</div>
                </div>
                <div className="hero-meta-item">
                  <div className="hero-meta-num">2030</div>
                  <div className="hero-meta-label">Aligned with<br />Saudi Vision</div>
                </div>
                <div className="hero-meta-item">
                  <div className="hero-meta-num">24/7</div>
                  <div className="hero-meta-label">In-Kingdom<br />delivery</div>
                </div>
              </div>
            </div>
            <div className="hero-visual reveal d3">
              <div className="hero-visual-content">
                <div className="hero-visual-image">
                  <img src={twoTowers} alt="Two Towers" />
                </div>
                <div>
                  <p className="hero-quote">
                    We don't just deliver projects. We hold our client's data, trust, and ambitions as a responsibility.
                  </p>
                  <p className="hero-quote-attr">— The TQNY ALBYANAT Promise</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLIENT MARQUEE */}
      <section className="clients-section-new">
        <div className="container-new">
          <p className="clients-label mono-home">Trusted by leading enterprises across the region</p>
        </div>
        <div className="ticker-wrapper">
          <div className="ticker-track">
            <img src={logoStrip} alt="Our Clients" className="ticker-strip" />
            <img src={logoStrip} alt="Our Clients" className="ticker-strip" />
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section className="services" id="services">
        <div className="container-new">
          <div className="section-header">
            <div className="section-eyebrow mono">What We Do</div>
            <h2 className="section-title-new">Built for complex enterprises. Delivered with care.</h2>
            <p className="section-intro">Six practice areas, one philosophy: technology should serve the business, respect the culture, and earn its trust every day.</p>
          </div>
          <div className="services-grid">
            {services.map((service, idx) => (
              <Link key={idx} to={service.link} className="service-card">
                <div className="service-num">{service.num} / {service.category}</div>
                <div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-desc">{service.description}</p>
                </div>
                <div className="service-arrow">→</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SOVEREIGN / COMPLIANCE SECTION */}
      <section className="sovereign" id="sovereign">
        <div className="container-new">
          <div className="sovereign-grid">
            <div className="sovereign-content">
              <div className="section-eyebrow mono">Sovereign By Design</div>
              <h2>Local compliance. Global standards.</h2>
              <p>For organizations operating in Saudi Arabia, data sovereignty isn't a feature — it's a regulatory requirement. Every TQNY ALBYANAT engagement is engineered for in-Kingdom residency and full alignment with Saudi authorities, while meeting the international security and quality standards multinationals expect.</p>
              <p>Whether you're a Saudi enterprise scaling regionally or a global organization expanding into the Kingdom, we bridge both worlds — with the local fluency to navigate regulation, and the engineering rigor to operate at international scale.</p>
              <div className="sovereign-features">
                {sovereignFeatures.map((feature, idx) => (
                  <div key={idx} className="feature">
                    <div className="feature-num">{feature.num}</div>
                    <div>
                      <div className="feature-title">{feature.title}</div>
                      <div className="feature-desc">{feature.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="compliance-card">
              <h3>Every framework that matters.</h3>
              <p>Our delivery meets or exceeds the regulatory and security standards required by government, banking, and enterprise clients — both local and multinational.</p>
              <div className="compliance-grid">
                {complianceItems.map((item, idx) => (
                  <div key={idx} className="compliance-item">
                    <div className="compliance-check">✓</div>
                    <div>
                      <div className="compliance-item-name">{item.name}</div>
                      <div className="compliance-item-desc">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STORY SECTION */}
      <section className="story" id="story">
        <div className="container-new">
          <div className="story-grid">
            <div>
              <div className="section-eyebrow mono">Our Story</div>
              <h2>Local roots. Global outlook.</h2>
            </div>
            <div className="story-content">
              <p>TQNY ALBYANAT was founded in 2022 with a clear conviction: that great technology partnerships are built on understanding — of the business, the people, the context, and the goals. We design governed data platforms, scale AI responsibly, and deliver outcomes that move our clients forward.</p>
              <p>Every engagement starts with listening. Every solution is designed for the realities our clients operate in — the regulations, the languages, the pace of change. We measure success not in hours billed, but in the capability and clarity we leave behind.</p>
              <div className="story-values">
                {storyValues.map((value, idx) => (
                  <div key={idx} className="value-item">
                    <div className="value-num mono">{value.num}</div>
                    <div className="value-label">{value.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VISION 2030 SECTION */}
      <section className="vision">
        <div className="container-new">
          <div className="vision-grid">
            <div className="vision-visual">
              <div className="vision-visual-image">
                <img src={fourTowers} alt="Four Towers" />
              </div>
              <div className="vision-badge">Vision</div>
              <div className="vision-mark">Built for today.<br />Built for the future.</div>
            </div>
            <div className="vision-content">
              <div className="section-eyebrow mono">Aligned with Vision 2030</div>
              <h2>A trusted partner in regional transformation.</h2>
              <p>Saudi Arabia is building one of the world's most dynamic digital economies. We exist to help every organization participating in that journey — local enterprises, public-sector entities, and multinationals expanding into the region — succeed in it.</p>
              <div className="vision-pillars">
                {visionPillars.map((pillar, idx) => (
                  <div key={idx} className="pillar">
                    <div className="pillar-num">{pillar.num}</div>
                    <div>
                      <div className="pillar-title">{pillar.title}</div>
                      <div className="pillar-desc">{pillar.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES SECTION */}
      <section className="industries" id="industries">
        <div className="container-new">
          <div className="section-header">
            <div className="section-eyebrow mono">Who We Serve</div>
            <h2 className="section-title-new">Industries shaping the next chapter.</h2>
            <p className="section-intro">From government entities to global banks, from regional champions to multinational operators — we serve the organizations driving forward the region's most ambitious work.</p>
          </div>
          <div className="industries-grid-new">
            {industries.map((industry, idx) => (
              <div key={idx} className="industry-card-new">
                <div className="industry-index">{industry.index} / {industry.category}</div>
                <div className="industry-name">{industry.name}</div>
                <div className="industry-meta">{industry.meta}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AMANAH QUOTE SECTION */}
      <section className="amanah">
        <div className="amanah-content">
          <div className="amanah-mark">"</div>
          <p className="amanah-text">We don't just deliver projects. We hold our clients' data, trust, and ambitions as a sacred responsibility.</p>
          <div className="amanah-attribution">
            <span className="amanah-translation">The TQNY ALBYANAT Promise</span>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="cta" id="contact">
        <div className="container-new">
          <div className="cta-inner">
            <div className="cta-content">
              <div className="cta-eyebrow">Start a Conversation</div>
              <h2>Let's build something that lasts.</h2>
              <p>Every great engagement begins with a conversation. Tell us about your ambition — we'll tell you how we can help.</p>
              <button onClick={() => navigate("/contact")} type="submit" className="btn-cta-primary">Request a Meeting <span>→</span></button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;